import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from './api';

export type AdminUser = {
  email: string;
  name: string;
  is_super: boolean;
  picture?: string | null;
};

type AuthState = {
  user: AdminUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signInWithGoogleCredential: (credential: string) => Promise<AdminUser>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

/** Treat any /admin/* path as an "authenticated zone". */
function isAdminRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/admin');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  // If we're not on an admin route we can short-circuit "loading" immediately;
  // the public site never needs to know who the admin is.
  const [loading, setLoading] = useState(() => isAdminRoute());

  const refresh = useCallback(async () => {
    try {
      const me = await api<{ ok: true; user: AdminUser }>('/auth/me');
      setUser(me.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Public brochure pages do not need an admin session check, and probing
    // /api/auth/me on every visit pollutes the console with 404s when the
    // PHP API is not deployed. Only verify the session when actually inside
    // the admin tree.
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }
    if (!window.location.pathname.startsWith('/admin')) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh]);

  const signInWithGoogleCredential = useCallback(async (credential: string) => {
    const res = await api<{ ok: true; user: AdminUser }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
    setUser(res.user);
    return res.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, refresh, signInWithGoogleCredential, signOut }),
    [user, loading, refresh, signInWithGoogleCredential, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-ink-700">
        Checking authentication…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
